import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';

import { IconTouchable } from '@vs-shop/ui/iconTouchable';
import { clsxm } from '@vs-shop/utils';

/**
 * The buy box: what the product costs, how many of it to take, and the control
 * that takes them.
 *
 *   <ProductBuyBox>
 *     <ProductBuyBoxPriceRow>
 *       <ProductBuyBoxPrices>
 *         <ProductBuyBoxPriceOriginal>22 990 Kč</ProductBuyBoxPriceOriginal>
 *         <ProductBuyBoxPriceLine>
 *           <ProductBuyBoxPrice>18 990 Kč</ProductBuyBoxPrice>
 *           <ProductBuyBoxPriceNote>S DPH</ProductBuyBoxPriceNote>
 *         </ProductBuyBoxPriceLine>
 *       </ProductBuyBoxPrices>
 *       <ProductBuyBoxShippingNote>DOPRAVA ZDARMA</ProductBuyBoxShippingNote>
 *     </ProductBuyBoxPriceRow>
 *
 *     <ProductBuyBoxActions>
 *       <ProductBuyBoxQuantity>
 *         <ProductAmountCounter … />
 *       </ProductBuyBoxQuantity>
 *       <ProductBuyBoxSubmit>
 *         <ProductAddToCart variant={ProductAddToCartVariant.PANEL} … />
 *       </ProductBuyBoxSubmit>
 *     </ProductBuyBoxActions>
 *   </ProductBuyBox>
 *
 * Which parts appear is tenant JSX throughout: a price of nought composes
 * `ProductBuyBoxPrice` on its own with a copy line instead of an amount, and a
 * product with no pre-discount price composes no `ProductBuyBoxPriceOriginal`.
 * Money formatting, currency, the discount rule and every string are the
 * tenant's; the parts take resolved copy as children.
 *
 * The cart mutation is nowhere in here. The control this section frames is
 * `@vs-shop/commerce-ui/productAddToCart`, which takes the handler; the atoms,
 * the stock arithmetic and the error messages stay with the tenant.
 *
 * Server-first: every part is a plain server component. The stepper and the
 * button are client leaves the tenant composes, and neither is imported here.
 */

/**
 * Root of the section, two elements deep: the outer box is the
 * container-query context every `@[...]` step below resolves against, the inner
 * one is the column the parts stack in.
 *
 * The query context is the section's rather than tenant JSX's because a
 * composition omitting it would still render — with every responsive step
 * silently compiling to nothing.
 */
const productBuyBoxClasses = 'bg-background @container';

const productBuyBoxBodyClasses = 'flex flex-col';

export type ProductBuyBoxProps = ComponentProps<'div'>;

/** The buy box. No `variant`: it paints one way wherever a storefront puts it. */
export const ProductBuyBox = ({ className, children, ...props }: ProductBuyBoxProps) => (
  <div data-slot="product-buy-box" className={clsxm(productBuyBoxClasses, className)} {...props}>
    <div data-slot="product-buy-box-body" className={productBuyBoxBodyClasses}>
      {children}
    </div>
  </div>
);

/**
 * The price and whatever is stated opposite it, both sitting on the same
 * baseline so a two-line price and a one-line notice still agree at the foot.
 */
const productBuyBoxPriceRowClasses = 'flex flex-1 items-end justify-between gap-4';

export type ProductBuyBoxPriceRowProps = ComponentProps<'div'>;

/** The row the price shares with its notice. */
export const ProductBuyBoxPriceRow = ({ className, ...props }: ProductBuyBoxPriceRowProps) => (
  <div
    data-slot="product-buy-box-price-row"
    className={clsxm(productBuyBoxPriceRowClasses, className)}
    {...props}
  />
);

/** The price column: the struck original, then what it costs now. */
const productBuyBoxPricesClasses = 'flex flex-1 flex-col';

export type ProductBuyBoxPricesProps = ComponentProps<'div'>;

/** The price stack. */
export const ProductBuyBoxPrices = ({ className, ...props }: ProductBuyBoxPricesProps) => (
  <div
    data-slot="product-buy-box-prices"
    className={clsxm(productBuyBoxPricesClasses, className)}
    {...props}
  />
);

/**
 * What the product cost before the discount, struck through above the current
 * price. Composed only where there is one — a part that renders nothing would
 * still take its gap.
 */
const productBuyBoxPriceOriginalClasses =
  'text-foreground mb-1 text-xl/5 font-light line-through @md:text-[26px]/[26px]';

export type ProductBuyBoxPriceOriginalProps = ComponentProps<'span'>;

/** The struck pre-discount price. */
export const ProductBuyBoxPriceOriginal = ({
  className,
  ...props
}: ProductBuyBoxPriceOriginalProps) => (
  <span
    data-slot="product-buy-box-price-original"
    className={clsxm(productBuyBoxPriceOriginalClasses, className)}
    {...props}
  />
);

/**
 * The current price and its tax note, kept on one line: a price broken across
 * two reads as two numbers.
 */
const productBuyBoxPriceLineClasses = 'space-x-2 whitespace-nowrap';

export type ProductBuyBoxPriceLineProps = ComponentProps<'div'>;

/** The line the price and its note share. */
export const ProductBuyBoxPriceLine = ({ className, ...props }: ProductBuyBoxPriceLineProps) => (
  <div
    data-slot="product-buy-box-price-line"
    className={clsxm(productBuyBoxPriceLineClasses, className)}
    {...props}
  />
);

/**
 * What it costs. The same part carries a price and a stand-in for one — a
 * product quoted on request states that here, at the price's own size.
 */
const productBuyBoxPriceClasses = 'text-2xl/6 font-semibold @md:text-[28px]/[28px]';

export type ProductBuyBoxPriceProps = ComponentProps<'span'>;

/** The current price. */
export const ProductBuyBoxPrice = ({ className, ...props }: ProductBuyBoxPriceProps) => (
  <span
    data-slot="product-buy-box-price"
    className={clsxm(productBuyBoxPriceClasses, className)}
    {...props}
  />
);

/** What the price includes, stated quietly beside it. */
const productBuyBoxPriceNoteClasses = 'text-foreground-muted';

export type ProductBuyBoxPriceNoteProps = ComponentProps<'span'>;

/** The tax note. */
export const ProductBuyBoxPriceNote = ({ className, ...props }: ProductBuyBoxPriceNoteProps) => (
  <span
    data-slot="product-buy-box-price-note"
    className={clsxm(productBuyBoxPriceNoteClasses, className)}
    {...props}
  />
);

/** What the order earns, stated opposite the price. */
const productBuyBoxShippingNoteClasses = 'text-right font-medium uppercase';

export type ProductBuyBoxShippingNoteProps = ComponentProps<'span'>;

/** The shipping notice. */
export const ProductBuyBoxShippingNote = ({
  className,
  ...props
}: ProductBuyBoxShippingNoteProps) => (
  <span
    data-slot="product-buy-box-shipping-note"
    className={clsxm(productBuyBoxShippingNoteClasses, className)}
    {...props}
  />
);

/** The quantity and the control that commits it, side by side. */
const productBuyBoxActionsClasses = 'flex gap-x-4 pt-5 @md:pt-4';

export type ProductBuyBoxActionsProps = ComponentProps<'div'>;

/** The action row. */
export const ProductBuyBoxActions = ({ className, ...props }: ProductBuyBoxActionsProps) => (
  <div
    data-slot="product-buy-box-actions"
    className={clsxm(productBuyBoxActionsClasses, className)}
    {...props}
  />
);

/**
 * The stepper's column, held to a width the controls stay legible in. Narrow
 * enough that the button keeps the rest of the row, fixed once there is room.
 */
const productBuyBoxQuantityClasses = 'min-h-full min-w-[100px] @md:max-w-[146px] @md:min-w-[146px]';

export type ProductBuyBoxQuantityProps = ComponentProps<'div'>;

/** The quantity stepper's box. */
export const ProductBuyBoxQuantity = ({ className, ...props }: ProductBuyBoxQuantityProps) => (
  <div
    data-slot="product-buy-box-quantity"
    className={clsxm(productBuyBoxQuantityClasses, className)}
    {...props}
  />
);

/**
 * The button's column, and the positioning context its error popup hangs in.
 *
 * `min-w-0` is what lets the button shrink: a flex item's minimum is its
 * content by default, so a long label would set the whole row's minimum width
 * and run past the section's clip rather than wrapping inside it.
 */
const productBuyBoxSubmitClasses = '@container-button relative w-full min-w-0';

export type ProductBuyBoxSubmitProps = ComponentProps<'div'>;

/** The add-to-cart control's box. */
export const ProductBuyBoxSubmit = ({ className, ...props }: ProductBuyBoxSubmitProps) => (
  <div
    data-slot="product-buy-box-submit"
    className={clsxm(productBuyBoxSubmitClasses, className)}
    {...props}
  />
);

/**
 * What went wrong, over the button that tried it.
 *
 * Two tones, because the two things that stop an order are not the same news:
 * a cart already holding everything the warehouse has is a limit a shopper has
 * reached, and a rejected order is a failure.
 */
export const productBuyBoxErrorVariants = cva(
  'absolute top-[calc(100%+0.5rem)] right-0 -left-[108px] z-10 flex items-center gap-2 ' +
    'pho-lg:right-0 pho-lg:left-auto pho-lg:p-4 rounded-xl p-3 shadow-lg',
  {
    variants: {
      variant: {
        /** An order that failed. */
        destructive: 'bg-destructive text-destructive-foreground',
        /** A limit the shopper has reached. */
        warning: 'bg-warning text-warning-foreground',
        unstyled: '',
      },
    },
  }
);

type ProductBuyBoxErrorVariantName = NonNullable<
  VariantProps<typeof productBuyBoxErrorVariants>['variant']
>;

export type ProductBuyBoxErrorVariant = ProductBuyBoxErrorVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const ProductBuyBoxErrorVariant = {
  DESTRUCTIVE: 'destructive',
  WARNING: 'warning',
  /** The opt-out: a tenant painting the popup itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductBuyBoxErrorVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductBuyBoxErrorVariantProps = { readonly variant: ProductBuyBoxErrorVariant };

export type ProductBuyBoxErrorProps = ComponentProps<'div'> & ProductBuyBoxErrorVariantProps;

/** The popup stating why the product did not reach the cart. */
export const ProductBuyBoxError = ({ className, variant, ...props }: ProductBuyBoxErrorProps) => (
  <div
    data-slot="product-buy-box-error"
    className={clsxm(productBuyBoxErrorVariants({ variant }), className)}
    {...props}
  />
);

/** The message itself, taking the room the close control leaves. */
const productBuyBoxErrorMessageClasses = 'pho-lg:text-base flex-1 text-sm tracking-tight';

export type ProductBuyBoxErrorMessageProps = ComponentProps<'span'>;

/** The popup's copy. */
export const ProductBuyBoxErrorMessage = ({
  className,
  ...props
}: ProductBuyBoxErrorMessageProps) => (
  <span
    data-slot="product-buy-box-error-message"
    className={clsxm(productBuyBoxErrorMessageClasses, className)}
    {...props}
  />
);

/**
 * What dismisses the popup.
 *
 * It renders its own glyph rather than taking one as children: the cross is
 * the section's answer to "how is this dismissed", not copy a tenant supplies,
 * and every tenant's answer is the same cross.
 */
const productBuyBoxErrorCloseClasses = 'size-5 shrink-0';

export type ProductBuyBoxErrorCloseProps = Omit<
  ComponentProps<typeof IconTouchable<'button'>>,
  'as' | 'children'
>;

/** The popup's dismiss control. */
export const ProductBuyBoxErrorClose = ({ className, ...props }: ProductBuyBoxErrorCloseProps) => (
  <IconTouchable
    type="button"
    data-slot="product-buy-box-error-close"
    className={className}
    {...props}
  >
    <X
      data-slot="product-buy-box-error-close-icon"
      aria-hidden
      className={productBuyBoxErrorCloseClasses}
    />
  </IconTouchable>
);
